import { z } from "zod";
import { createExportListRequestSchema } from "../util/schemas.ts";

export const JobStatus = {
  STARTED: "started",
  FINISHED: "finished",
  FAILED: "failed",
};

export const JobTimeoutMinutes = 16;

export type JobStatusType = (typeof JobStatus)[keyof typeof JobStatus];

export const JobTypes = {
  RUN_CALCULATION: "run_calculation",
  APPLY_RECORD_CONFIGURATION: "apply_default_record_configuration",
  APPLY_CALCULATOR_CONFIGURATION: "apply_default_calculator_configuration",
  APPLY_DEFAULT_RECORDS_BY_CONFIG_FILTER:
    "apply_default_records_by_config_filter",
  APPLY_DEFAULT_CALCULATOR_CONFIGURATION_AND_RUN_CALCULATION:
    "apply_default_calculator_configuration_and_run_calculation",
  BULK_DELETE_FORM_SUBMISSIONS: "bulk_delete_form_submissions",
  UPDATE_ASSET_STATUS: "update_asset_status",
};

export const JobGetListFilterSchema = z
  .object({
    id: z.string(),
    status: z.nativeEnum(JobStatus),
    type: z.nativeEnum(JobTypes),
    started_at: z.object({
      from: z.string().datetime(),
      to: z.string().datetime().optional(),
    }),
    finished_at: z.object({
      from: z.string().datetime(),
      to: z.string().datetime().optional(),
    }),
  })
  .partial();
export type JobGetListFilterType = z.infer<typeof JobGetListFilterSchema>;

export type JobTypeType = (typeof JobTypes)[keyof typeof JobTypes];

export const JobSchema = z.object({
  id: z.string(),
  company_id: z.string().uuid(),
  status: z.enum([JobStatus.FAILED, JobStatus.FINISHED, JobStatus.STARTED]),
  type: z.enum([
    JobTypes.APPLY_CALCULATOR_CONFIGURATION,
    JobTypes.APPLY_RECORD_CONFIGURATION,
    JobTypes.RUN_CALCULATION,
    JobTypes.APPLY_DEFAULT_RECORDS_BY_CONFIG_FILTER,
    JobTypes.APPLY_DEFAULT_CALCULATOR_CONFIGURATION_AND_RUN_CALCULATION,
    JobTypes.BULK_DELETE_FORM_SUBMISSIONS,
    JobTypes.UPDATE_ASSET_STATUS,
  ]),
  started_at: z.string(),
  started_by: z.string().uuid(),
  finished_at: z.string(),
  duration: z.number(),
  input: z.record(z.string(), z.any()),
  total_items: z.number(),
  processed_items: z.number(),
  item_status_counts: z.object({
    success: z.number(),
    failed: z.number(),
    skipped: z.number(),
  }),
});

export type JobType = z.infer<typeof JobSchema>;

export const BulkItemStatus = {
  SUCCESS: "success",
  FAILED: "failed",
  SKIPPED: "skipped",
  STARTED: "started",
};
export type BulkItemStatusType =
  (typeof BulkItemStatus)[keyof typeof BulkItemStatus];

export const JobItemSchema = z.object({
  key: z.string().uuid(),
  job_id: z.string().uuid(),
  company_id: z.string().uuid(),
  status: z.nativeEnum(BulkItemStatus),
  input: z.record(z.string(), z.any()),
  output: z.record(z.string(), z.any()),
});
export type JobItemType = z.infer<typeof JobItemSchema>;

export const JobItemExportListRequestSchema = createExportListRequestSchema(
  JobItemSchema.pick({ key: true, status: true }).partial()
);
export type JobItemExportListRequestType = z.infer<
  typeof JobItemExportListRequestSchema
>;
